import { useState, useEffect } from 'react';
import useStore from '../store/useStore';

/**
 * Overall dashboard status, worst-of across what it shows:
 *   disconnected — no WebSocket
 *   stale        — live metrics older than the dashboard's stale threshold
 *   overdue      — a dataset behind a report widget has missed its cadence
 *   connected    — everything current
 */
export function useDashboardStatus() {
  const currentDashboard = useStore((state) => state.currentDashboard);
  const dashboardLastUpdates = useStore((state) => state.dashboardLastUpdates);
  const wsConnected = useStore((state) => state.wsConnected);
  const datasetFreshness = useStore((state) => state.datasetFreshness);
  const alerts = useStore((state) => state.alerts) || [];
  const [currentTime, setCurrentTime] = useState(new Date());

  // Re-evaluate staleness every 10 seconds.
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 10000);
    return () => clearInterval(interval);
  }, []);

  if (!wsConnected) return { state: 'disconnected', label: 'Disconnected', className: 'disconnected', isStale: false, overdueDatasets: [], alerts };

  const widgets = currentDashboard?.widgets || [];
  const hasLive = widgets.some((w) => !w.widget_kind || w.widget_kind === 'metric');
  const lastUpdate = currentDashboard?.id ? dashboardLastUpdates[currentDashboard.id] : null;
  const staleThresholdMinutes = currentDashboard?.stale_threshold_minutes || 5;
  const liveStale = !!(currentDashboard && hasLive && lastUpdate &&
    (currentTime - new Date(lastUpdate)) / 1000 / 60 > staleThresholdMinutes);

  const datasetNames = [...new Set(widgets.filter((w) => w.widget_kind === 'dataset' && w.dataset_name).map((w) => w.dataset_name))];
  const overdueDatasets = datasetNames.filter((name) => datasetFreshness[name] === 'overdue' || datasetFreshness[name] === 'never');

  if (liveStale) return { state: 'stale', label: 'Stale', className: 'stale', isStale: true, overdueDatasets, alerts };
  if (overdueDatasets.length) return { state: 'overdue', label: 'Data overdue', className: 'stale', isStale: false, overdueDatasets, alerts };
  if (alerts.length) {
    const critical = alerts.some((a) => a.severity === 'critical');
    return { state: 'alerting', label: `${alerts.length} alert${alerts.length === 1 ? '' : 's'}`, className: critical ? 'disconnected' : 'stale', isStale: false, overdueDatasets, alerts };
  }
  return { state: 'connected', label: 'Connected', className: 'connected', isStale: false, overdueDatasets: [], alerts };
}

/**
 * True when the dashboard's live data is stale (kept for existing callers).
 */
export function useDashboardStale() {
  return useDashboardStatus().isStale;
}
